import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileText, File, Image, Loader2, AlertCircle, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SharedDocumentPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchSharedDoc() {
      try {
        const res = await fetch(`${API_URL}/api/documents/shared/${token}`);
        if (res.status === 410) {
          setError('expired');
          return;
        }
        if (!res.ok) {
          setError('not_found');
          return;
        }
        const json = await res.json();
        setData(json);
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    }
    fetchSharedDoc();
  }, [token]);

  const getFileIcon = (mime) => {
    if (!mime) return File;
    if (mime === 'application/pdf') return FileText;
    if (mime.startsWith('image/')) return Image;
    return File;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <Clock className="h-12 w-12 text-amber-500 dark:text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Link Expired</h2>
            <p className="text-muted-foreground text-sm">
              This share link has expired. Please ask the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Document Not Found</h2>
            <p className="text-muted-foreground text-sm">
              This link may be invalid or the document may have been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const FileIcon = getFileIcon(data.mime_type);
  const isImage = data.mime_type?.startsWith('image/');
  const isPdf = data.mime_type === 'application/pdf';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-foreground">PocketFiler</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
            <Shield className="h-3.5 w-3.5" />
            Secure document sharing
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card className="overflow-hidden">
          {/* Document Info */}
          <div className="p-6 border-b bg-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                <FileIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-foreground truncate">{data.filename}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {formatBytes(data.size)}
                  {data.mime_type && ` • ${data.mime_type.split('/').pop().toUpperCase()}`}
                </p>
              </div>
              {data.download_url && (
                <Button asChild className="shrink-0">
                  <a href={data.download_url} download={data.filename}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Preview */}
          {data.download_url && (isImage || isPdf) && (
            <div className="p-6 border-b bg-background">
              {isImage ? (
                <img
                  src={data.download_url}
                  alt={data.filename}
                  className="max-w-full max-h-[500px] mx-auto rounded-lg border shadow-sm object-contain"
                />
              ) : isPdf ? (
                <iframe
                  src={data.download_url}
                  title="Document Preview"
                  className="w-full h-[600px] rounded-lg border shadow-sm"
                />
              ) : null}
            </div>
          )}

          {/* AI Summary */}
          {data.ai_summary && (
            <div className="p-6 bg-card">
              <h3 className="text-sm font-semibold text-foreground mb-2">AI Summary</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{data.ai_summary}</p>
            </div>
          )}
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground/60 mt-8">
          Shared securely via <a href="https://pocketfiler.com" className="text-primary hover:underline">PocketFiler</a>
        </p>
      </main>
    </div>
  );
}
